<template>
    <div class="aiMessage" :class="{ 'aiMessage--noAnimate': message.historical }">
        <div v-if="showAvatar" class="aiMessage__avatar">
            <img src="../../images/BotIcon.png" alt="Asistente" />
        </div>

        <div class="aiMessage__bubble" :class="{ 'aiMessage__bubble--user': message.isUserMessage, 'aiMessage__bubble--loading': loadingMessage }">
            <div v-if="message.files.length > 0" class="aiMessage__attachments">
                <button type="button" class="aiMessage__attachmentsToggle" @click="ToggleAttachedFiles()"
                    :aria-expanded="showAttachedFiles"
                    :aria-label="showAttachedFiles ? 'Ocultar archivos adjuntos' : 'Mostrar archivos adjuntos'">
                    <i class="bi bi-paperclip"></i>
                    <span>{{ attachmentsLabel }}</span>
                    <i class="bi bi-chevron-down aiMessage__chevron" :class="{ 'aiMessage__chevron--open': showAttachedFiles }"></i>
                </button>

                <div class="aiMessage__filesListWrap" :class="{ 'aiMessage__filesListWrap--open': showAttachedFiles }">
                    <div class="aiMessage__filesList">
                        <span v-for="file in message.files" :key="file.name" class="aiMessage__fileChip">
                            <FileTypeIcon :type="file.type" :size="14" />
                            {{ file.name }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="aiMessage__content" v-html="formattedMessage"></div>
        </div>
    </div>
</template>

<script>
import FileTypeIcon from '@/components/Generic/FileTypeIcon.vue';

export default {
    name: 'AssistantMessage',
    components: {
        FileTypeIcon
    },
    props: {
        message: {
            type: Object,
            required: true,
        },
        loadingMessage: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    data() {
        return {
            formattedMessage: '',
            showAttachedFiles: false,
        };
    },

    computed: {
        showAvatar() {
            return !this.message.isUserMessage || this.loadingMessage;
        },

        attachmentsLabel() {
            let count = this.message.files.length;
            return count + (count > 1 ? ' archivos adjuntos' : ' archivo adjunto');
        }
    },

    mounted() {
        this.formattedMessage = this.message.isUserMessage ? this.message.content : this.message.content.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
        this.formattedMessage = this.message.isUserMessage ? this.formattedMessage : this.formattedMessage.replace(/###/g, '●');
        this.formattedMessage = this.message.isUserMessage ? this.formattedMessage : this.formattedMessage.replace(/【\d+:\d+†source】/g, '');
        this.formattedMessage = this.message.isUserMessage ? this.formattedMessage : this.formattedMessage.replace(/\*(.*?)\*/g, '<i>$1</i>');
    },

    methods: {
        ToggleAttachedFiles() {
            this.showAttachedFiles = !this.showAttachedFiles;
        }
    }
};
</script>

<style scoped>
/* Tokens (--ai-ink, --ai-accent, --ai-line, --ai-hover, --ai-ease, ...) are
   defined on .aiAssistantChatContainer in AiAssistant.vue. Custom properties
   inherit through the DOM regardless of Vue's scoped-style boundary, and
   this component is only ever rendered inside that container, so it's safe
   to consume them directly here rather than redefining the palette. */
.aiMessage {
    width: 100%;
    display: flex;
    align-items: flex-start;
    /* Each message eases up into place as it's added or received. Keyed nodes
       animate only on mount, so existing messages stay put during pagination. */
    animation: aiMessageIn 280ms var(--ai-ease) both;
}

.aiMessage--noAnimate {
    animation: none;
}

@keyframes aiMessageIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
    .aiMessage {
        animation: aiMessageFade 200ms ease both;
    }

    .aiMessage--noAnimate {
        animation: none;
    }

    @keyframes aiMessageFade {
        from { opacity: 0; }
        to   { opacity: 1; }
    }
}

/* ---------- Avatar ---------- */
/* Same dark diagonal gradient as the panel header's avatar — the recurring
   brand mark, repeated once per assistant turn instead of once per session. */
.aiMessage__avatar {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    margin-right: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2a2a3a, var(--LativoDarkPurple));
    box-shadow: 0 3px 10px -5px rgba(31, 31, 44, 0.5);
}

.aiMessage__avatar img {
    width: 16px;
    height: 16px;
    transform: translateX(1px);
}

/* ---------- Bubble ---------- */
.aiMessage__bubble {
    width: calc(100% - 40px);
    white-space: pre-wrap;
    overflow: hidden;
    padding: 8px 10px;
    margin-bottom: 15px;
    font-size: 14px;
    line-height: 1.5;
    color: var(--ai-ink);
}

.aiMessage__bubble--user {
    background-color: var(--ai-surface);
    border: 1px solid var(--ai-line);
    border-radius: 16px;
    max-width: calc(100% - 30px);
    width: auto;
    margin-left: auto;
}

.aiMessage__bubble--loading {
    width: auto;
    margin: auto auto auto 0;
}

/* ---------- Attachments ---------- */
.aiMessage__attachments {
    margin-bottom: 6px;
}

/* Single interactive control (icon + count + chevron) instead of a label and
   a separate icon button doing the same thing — one clear target to press. */
.aiMessage__attachmentsToggle {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 10px;
    background-color: transparent;
    border: 1px solid var(--ai-line);
    border-radius: 10px;
    color: var(--ai-ink-dim);
    font-size: 12.5px;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, transform 140ms var(--ai-ease);
    -webkit-tap-highlight-color: transparent;
}

.aiMessage__attachmentsToggle i {
    margin: 0;
    font-size: 14px;
}

.aiMessage__attachmentsToggle .bi-paperclip {
    transform: rotate(-145deg);
}

.aiMessage__attachmentsToggle:active {
    transform: scale(0.97);
}

.aiMessage__attachmentsToggle:focus-visible {
    outline: 2px solid var(--ai-accent-soft);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .aiMessage__attachmentsToggle:hover {
        background-color: var(--ai-hover);
        color: var(--ai-ink);
    }
}

.aiMessage__chevron {
    transition: transform 200ms var(--ai-ease);
}

.aiMessage__chevron--open {
    transform: rotate(180deg);
}

/* Height-animated reveal via the grid trick — no JS height measuring, and it
   collapses/expands smoothly instead of the old instant `hidden` toggle. */
.aiMessage__filesListWrap {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 220ms var(--ai-ease);
}

.aiMessage__filesListWrap--open {
    grid-template-rows: 1fr;
}

.aiMessage__filesList {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    overflow: hidden;
    min-height: 0;
    padding-top: 8px;
}

.aiMessage__fileChip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 9px;
    background-color: var(--ai-surface);
    border: 1px solid var(--ai-line);
    border-radius: 8px;
    color: var(--ai-ink-dim);
    font-size: 12px;
    line-height: 1.3;
}

.aiMessage__fileChip :deep(.fileTypeIcon) {
    flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
    .aiMessage__attachmentsToggle:active {
        transform: none;
    }
}
</style>